import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from '@/components/theme-provider';
import { AuthProvider } from '@/hooks/useAuth';
import { UnitPreferencesProvider } from '@/hooks/useUnitPreferences';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { SessionsPage } from '@/pages/SessionsPage';
import { SessionDetailPage } from '@/pages/SessionDetailPage';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { UserManagement } from '@/pages/admin/UserManagement';
import { OrganizationSettings } from '@/pages/admin/OrganizationSettings';
import { AnalyticsDashboard } from '@/pages/admin/AnalyticsDashboard';
import { LiveSessionMonitor } from '@/pages/coach/LiveSessionMonitor';
import ConversationalAnalytics from '@/pages/coach/ConversationalAnalytics';
import { PlayerAnalytics } from '@/pages/analytics/PlayerAnalytics';
import ReportsPage from '@/pages/reports/ReportsPage';
import { AcademyDashboard } from '@/pages/academy/AcademyDashboard';
import { ShotVisualizationPage } from '@/pages/ShotVisualizationPage';
import ShotVisualizationDemo from '@/pages/ShotVisualizationDemo';
import './globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="sixsight-ui-theme">
        <AuthProvider>
          <UnitPreferencesProvider>
          <Router>
            <div className="min-h-screen bg-background">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Sessions Routes */}
                <Route
                  path="/sessions"
                  element={
                    <ProtectedRoute>
                      <SessionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sessions/:id"
                  element={
                    <ProtectedRoute>
                      <SessionDetailPage />
                    </ProtectedRoute>
                  }
                />
                
                {/* Shot Visualization Routes - Temporarily bypassing auth for testing */}
                <Route
                  path="/shot-visualization"
                  element={<ShotVisualizationPage />}
                />
                <Route
                  path="/shot-visualization-demo"
                  element={<ShotVisualizationDemo />}
                />
                
                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredRoles={['ADMIN', 'COACH']}>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route 
                    path="users" 
                    element={
                      <ProtectedRoute requiredRoles={['ADMIN']}>
                        <UserManagement />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="analytics" element={<AnalyticsDashboard />} />
                  <Route path="analytics/players" element={<PlayerAnalytics />} />
                  <Route path="analytics/players/:playerId" element={<PlayerAnalytics />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route 
                    path="academy" 
                    element={
                      <ProtectedRoute requiredRoles={['ADMIN', 'COACH']}>
                        <AcademyDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="coach/live-session" 
                    element={
                      <ProtectedRoute requiredRoles={['COACH', 'ADMIN']}>
                        <LiveSessionMonitor />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="coach/conversational-analytics" 
                    element={
                      <ProtectedRoute requiredRoles={['COACH', 'ADMIN']}>
                        <ConversationalAnalytics />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="coach/*" element={<AdminDashboard />} /> {/* Other coach tools redirect to dashboard for now */}
                  <Route 
                    path="organization" 
                    element={
                      <ProtectedRoute requiredRoles={['ADMIN']}>
                        <OrganizationSettings />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="settings" 
                    element={
                      <ProtectedRoute requiredRoles={['ADMIN']}>
                        <OrganizationSettings />
                      </ProtectedRoute>
                    } 
                  />
                </Route>
              </Routes>
            </div>
            <Toaster />
          </Router>
          </UnitPreferencesProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;